<template>
  <div
    data-testid="schedule-timeline-page"
    :data-view-start="viewStart.format('YYYY-MM-DD')"
    :data-view-end="viewEnd.format('YYYY-MM-DD')"
  >
    <!-- Пейвол: FE-первичен (повтор includedInPlan старого экрана), 422 paywall от API — подстраховка -->
    <v-overlay v-if="!isLoading" :value="paywalled" class="px-5">
      <v-alert color="info" border="top" class="mx-2">
        <v-row>
          <v-col cols="12" class="text-right mt-0 mb-0 pb-0">
            <v-icon @click="goBack">mdi-close</v-icon>
          </v-col>
          <v-col cols="12" class="text-center">
            <v-icon size="50">mdi-information</v-icon>
          </v-col>
          <v-col cols="12" class="text-center font-bold">
            {{ $t("custom.free_tariff_warning") }}
          </v-col>
          <v-col cols="12" class="text-center">
            <v-btn @click="$router.push({ name: 'Transactions' })" color="success" class="mx-1 my-1">
              {{ $t("custom.to_paid_plan") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>
    </v-overlay>

    <loading :active.sync="isLoading" :is-full-page="true" color="#1976d2"></loading>

    <v-card>
      <v-card-title class="flex-nowrap">
        <h3 class="mr-4">{{ $t("custom.schedule") }}</h3>
        <v-btn small outlined color="primary" @click="scrollToToday">{{ $t("shift_timeline.today") }}</v-btn>
        <!-- Мобильная: кнопка каталога сжимается до иконки, легенда скрыта
             (имена видов и так написаны на барах) -->
        <v-btn
          v-if="canManage && isMobile"
          small
          icon
          color="primary"
          class="ml-2"
          data-testid="manage-views"
          @click="manageDialog.show = true"
        >
          <v-icon small>mdi-palette-swatch</v-icon>
        </v-btn>
        <v-btn
          v-else-if="canManage"
          small
          text
          color="primary"
          class="ml-2"
          data-testid="manage-views"
          @click="manageDialog.show = true"
        >
          <v-icon small left>mdi-palette-swatch</v-icon>{{ $t("shift_timeline.manage_views") }}
        </v-btn>
        <v-spacer></v-spacer>
        <template v-if="!isMobile">
          <!-- Легенда: активные шаблоны каталога -->
          <v-chip
            v-for="template in activeTemplates"
            :key="`legend-${template.id}`"
            class="mx-1 my-1"
            small
            :color="template.color"
            text-color="white"
          >
            {{ displayName(template) }}<span v-if="templateHours(template)" class="ml-1">{{ templateHours(template) }}</span>
          </v-chip>
        </template>
      </v-card-title>

      <v-card-text>
        <div
          class="shift-timeline-viewport"
          :class="$vuetify.theme.dark ? 'shift-timeline--dark' : ''"
        >
          <div class="shift-timeline">
            <!-- Левая колонка: сотрудники -->
            <div class="shift-timeline__names">
              <div class="shift-timeline__names-header"></div>
              <div
                v-for="(user, index) in users"
                :key="`name-${user.id}`"
                class="shift-timeline__name-cell"
                :class="nameCellClasses(user, index)"
                :style="{ height: rowHeight(user) + 'px' }"
                @mouseenter="hoverUserId = user.id"
                @mouseleave="hoverUserId = null"
              >
                <span class="shift-timeline__avatar">
                  <img
                    v-if="user.avatar_url && !avatarErrors[user.id]"
                    :src="user.avatar_url"
                    @error="$set(avatarErrors, user.id, true)"
                  />
                  <template v-else>{{ initials(user) }}</template>
                </span>
                <span class="shift-timeline__name-text" :class="user.active ? '' : 'grey--text'">
                  {{ user.name ? user.name : user.email }}
                </span>
              </div>
            </div>

            <!-- Правая часть: шкала + строки -->
            <div
              class="shift-timeline__scroll"
              ref="scrollArea"
              :class="drag.dragging ? 'shift-timeline__scroll--grabbing' : ''"
              @scroll="onScroll"
              @wheel="onWheel"
            >
              <div class="shift-timeline__content" :style="{ width: totalWidth + 'px' }">
                <div class="shift-timeline__months shift-timeline__pan-zone" @mousedown="onPanStart">
                  <div
                    v-for="month in months"
                    :key="`m-${month.label}`"
                    class="shift-timeline__month"
                    :style="{ width: month.days * cellWidth + 'px' }"
                  >
                    {{ month.label }}
                  </div>
                </div>
                <div class="shift-timeline__days shift-timeline__pan-zone" @mousedown="onPanStart">
                  <div
                    v-for="day in days"
                    :key="`d-${day.date.valueOf()}`"
                    class="shift-timeline__day"
                    :class="dayClasses(day)"
                    :style="{ width: cellWidth + 'px' }"
                  >
                    {{ day.date.format("DD dd") }}
                  </div>
                </div>

                <div class="shift-timeline__body">
                  <!-- Today-линия через все строки -->
                  <div
                    v-if="todayVisible"
                    class="shift-timeline__today-line"
                    :style="{ left: todayOffsetPx + 'px' }"
                  ></div>

                  <div
                    v-for="(user, index) in users"
                    :key="`row-${user.id}`"
                    class="shift-timeline__row"
                    :class="rowClasses(user, index)"
                    :data-testid="`shift-row-${user.id}`"
                    :style="{ height: rowHeight(user) + 'px' }"
                    @click="onRowClick($event, user)"
                    @mousedown="onSelectStart($event, user)"
                    @touchstart="onTouchStart($event, user)"
                    @touchmove="onTouchMove"
                    @touchend="onTouchEnd"
                    @mouseenter="hoverUserId = user.id"
                    @mouseleave="hoverUserId = null"
                  >
                    <!-- Ghost-подсветка протягиваемого периода -->
                    <div
                      v-if="selection.active && selection.user && selection.user.id === user.id"
                      class="shift-timeline__ghost"
                      data-testid="range-ghost"
                      :style="ghostStyle"
                    ></div>
                    <div
                      v-for="day in days"
                      :key="`grid-${user.id}-${day.date.valueOf()}`"
                      class="shift-timeline__grid-cell"
                      :class="day.isWeekend ? 'shift-timeline__grid-cell--weekend' : ''"
                      :style="{ width: cellWidth + 'px' }"
                    ></div>
                    <div
                      v-for="item in laneItemsByUser[user.id] || []"
                      :key="`bar-${item.id}`"
                      class="shift-timeline__bar"
                      :class="isAbsence(item.shift) ? 'shift-timeline__bar--absence' : ''"
                      data-testid="shift-bar"
                      :style="barStyle(item)"
                      @click.stop="onBarClick($event, item.shift)"
                      @mouseenter="onBarEnter($event, item.shift)"
                      @mouseleave="onBarLeave"
                    >
                      <span class="shift-timeline__bar-label">{{ barLabel(item.shift) }}</span>
                    </div>
                  </div>

                  <div v-if="!isLoading && !shifts.length" class="shift-timeline__empty">
                    {{ canManage ? $t("shift_timeline.empty_hint") : $t("shift_timeline.empty") }}
                  </div>
                </div>
              </div>

              <!-- Индикатор догрузки по краю -->
              <div v-if="isLoadingEdge" class="shift-timeline__edge-loader">
                <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
              </div>
            </div>
          </div>
        </div>

        <!-- Тултип смены -->
        <div
          v-if="tooltip.show && tooltip.shift"
          class="shift-timeline__tooltip"
          :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
        >
          <div class="shift-timeline__tooltip-title">
            <span class="shift-timeline__tooltip-dot" :style="{ backgroundColor: shiftColor(tooltip.shift) }"></span>
            {{ $t(categoryLocaleKey(tooltip.shift.category)) }}
          </div>
          <div>{{ hoursLabel(tooltip.shift) || $t("shift_timeline.all_day") }}</div>
          <div v-if="!isAllDay(tooltip.shift)">{{ durationLabel(tooltip.shift) }}</div>
          <div v-if="tooltipTemplateName" class="shift-timeline__tooltip-muted">{{ tooltipTemplateName }}</div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Меню пустой ячейки: шаблоны + свои часы (только для управляющих ролей) -->
    <v-menu v-model="cellMenu.show" :position-x="cellMenu.x" :position-y="cellMenu.y" absolute offset-y :close-on-content-click="false">
      <v-list dense data-testid="cell-menu">
        <template v-if="rangeLength > 1">
          <v-list-item dense disabled>
            <v-list-item-title class="font-weight-bold">
              {{ $t("shift_timeline.period_label", { from: cellMenu.date.format("DD.MM"), to: cellMenu.dateEnd.format("DD.MM"), days: rangeLength }) }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item dense @click.stop="skipWeekends = !skipWeekends">
            <v-checkbox
              v-model="skipWeekends"
              dense
              hide-details
              class="mt-0 pt-0"
              :label="$t('shift_timeline.skip_weekends')"
              @click.stop
              data-testid="skip-weekends"
            ></v-checkbox>
          </v-list-item>
          <v-divider></v-divider>
        </template>
        <v-list-item
          v-for="template in menuTemplates"
          :key="`menu-t-${template.id}`"
          link
          @click="createFromTemplate(template)"
        >
          <v-list-item-title>
            <v-icon small :color="template.color" class="mr-1">mdi-circle</v-icon>
            {{ displayName(template) }} <span v-if="templateHours(template)">{{ templateHours(template) }}</span>
          </v-list-item-title>
        </v-list-item>
        <template v-if="!cellMenu.workingOnly">
          <v-divider></v-divider>
          <v-list-item link @click="openCustomDialog">
            <v-list-item-title>{{ $t("shift_timeline.custom_hours") }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>

    <!-- Меню существующей смены -->
    <v-menu v-model="barMenu.show" :position-x="barMenu.x" :position-y="barMenu.y" absolute offset-y>
      <v-list dense data-testid="bar-menu">
        <v-list-item v-if="barMenu.shift" dense disabled>
          <v-list-item-title>{{ barTitle(barMenu.shift) }}</v-list-item-title>
        </v-list-item>
        <v-divider v-if="barMenu.shift"></v-divider>
        <v-list-item
          v-if="barMenu.shift && isAllDay(barMenu.shift) && !isAbsence(barMenu.shift)"
          link
          data-testid="add-view"
          @click="openAddView"
        >
          <v-list-item-title>{{ $t("shift_timeline.add_view") }}</v-list-item-title>
        </v-list-item>
        <v-list-item link @click="deleteShift(barMenu.shift)">
          <v-list-item-title class="error--text">{{ $t("shift_timeline.remove_shift") }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Управление видами работ (каталог шаблонов) -->
    <v-dialog v-model="manageDialog.show" max-width="560">
      <v-card data-testid="manage-views-dialog">
        <v-card-title>
          {{ $t("shift_timeline.manage_views") }}
          <v-spacer></v-spacer>
          <v-btn small color="success" data-testid="add-template" @click="openTemplateForm(null)">
            {{ $t("shift_timeline.add_template") }}
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-list dense>
            <v-list-item v-for="template in templates" :key="`mng-${template.id}`">
              <v-icon small :color="template.color" class="mr-2">mdi-circle</v-icon>
              <v-list-item-content>
                <v-list-item-title :class="template.active ? '' : 'text--disabled'">
                  {{ displayName(template) }}
                  <span v-if="templateHours(template)" class="grey--text ml-1">{{ templateHours(template) }}</span>
                </v-list-item-title>
                <v-list-item-subtitle>{{ $t(categoryLocaleKey(template.category)) }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action class="flex-row align-center">
                <v-switch
                  dense
                  hide-details
                  class="mt-0 pt-0 mr-1"
                  :input-value="template.active"
                  @change="toggleTemplateActive(template)"
                ></v-switch>
                <v-btn icon small @click="openTemplateForm(template)"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small @click="deleteTemplate(template)"><v-icon small color="error">mdi-delete</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="manageDialog.show = false">{{ $t("custom.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Форма вида работы -->
    <v-dialog v-model="templateForm.show" max-width="440">
      <v-card data-testid="template-form">
        <v-card-title>{{ templateForm.id ? $t("shift_timeline.edit_template") : $t("shift_timeline.add_template") }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="templateForm.name" :label="$t('shift_timeline.template_name')" data-testid="template-name"></v-text-field>
          <v-select
            v-model="templateForm.category"
            :items="allCategoryItems"
            :label="$t('shift_timeline.category')"
          ></v-select>
          <v-switch
            v-model="templateForm.allDay"
            :label="$t('shift_timeline.all_day_switch')"
            :disabled="isAbsenceCategory(templateForm.category)"
            hide-details
            class="mb-3"
          ></v-switch>
          <v-row v-if="!templateForm.allDay && !isAbsenceCategory(templateForm.category)" dense>
            <v-col cols="6">
              <v-text-field v-model="templateForm.start" :label="$t('shift_timeline.start_time')" placeholder="09:00"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="templateForm.end" :label="$t('shift_timeline.end_time')" placeholder="18:00"></v-text-field>
            </v-col>
          </v-row>
          <div class="mb-1">{{ $t("shift_timeline.color") }}</div>
          <div>
            <v-btn
              v-for="preset in templateColorPresets"
              :key="preset"
              icon
              small
              class="mr-1 mb-1"
              @click="templateForm.color = preset"
            >
              <v-icon :color="preset">{{ templateForm.color === preset ? "mdi-check-circle" : "mdi-circle" }}</v-icon>
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="templateForm.show = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="success" data-testid="template-save" @click="saveTemplateForm">{{ $t("custom.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Свои часы -->
    <v-dialog v-model="customDialog.show" max-width="420">
      <v-card data-testid="custom-hours-dialog">
        <v-card-title>{{ $t("shift_timeline.custom_hours") }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="customDialog.category"
            :items="workingCategoryItems"
            :label="$t('shift_timeline.category')"
          ></v-select>
          <v-text-field
            v-model="customDialog.start"
            :label="$t('shift_timeline.start_time')"
            placeholder="09:00"
            data-testid="custom-start"
          ></v-text-field>
          <v-text-field
            v-model="customDialog.end"
            :label="$t('shift_timeline.end_time')"
            placeholder="15:00"
            data-testid="custom-end"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="customDialog.show = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="success" data-testid="custom-save" @click="createCustom">{{ $t("custom.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import {
  generateTopLevelDates,
  generateBottomLevelDates,
  assignLanes,
} from "../gantt/gantt-utils";
import {
  buildLaneItems,
  calendarDaysBetween,
  shiftBarMetrics,
  isAllDay,
  hoursLabel,
  minutesLabel,
  parseTimeToMinutes,
  categoryLocaleKey,
  templateDisplayName,
  expandRange,
  mergeShifts,
  CATEGORY_COLORS,
  WORKING_CATEGORIES,
  ABSENCE_CATEGORIES,
  EXPAND_STEP_DAYS,
  MINUTES_PER_DAY,
} from "./shift-adapter";

const EDGE_THRESHOLD_PX = 200;
const EDGE_DEBOUNCE_MS = 150;
const DRAG_THRESHOLD_PX = 5;

export default {
  data() {
    return {
      users: [],
      shifts: [],
      templates: [],
      viewStart: moment().subtract(37, "days").startOf("day"),
      viewEnd: moment().add(30, "days").endOf("day"),

      isLoading: false,
      isLoadingEdge: false,
      edgeTimer: null,
      paywalledByApi: false,
      hoverUserId: null,
      tooltip: { show: false, x: 0, y: 0, shift: null },
      drag: { dragging: false, moved: false, startX: 0, startScroll: 0 },
      // Протяжка периода (mouse: drag >5px; touch: long-press 400мс)
      selection: { pending: false, active: false, user: null, anchorIndex: 0, endIndex: 0, startClientX: 0, rowRectLeft: 0 },
      skipWeekends: false,
      suppressRowClick: false,
      longPressTimer: null,
      touchOrigin: { x: 0, y: 0 },
      cellMenu: { show: false, x: 0, y: 0, user: null, date: null },
      barMenu: { show: false, x: 0, y: 0, shift: null },
      customDialog: { show: false, category: "work", start: "09:00", end: "15:00" },
      avatarErrors: {},
      manageDialog: { show: false },
      templateForm: { show: false, id: null, name: "", category: "work", allDay: true, start: "09:00", end: "18:00", color: "#4caf50" },
    };
  },
  created() {
    if (this.$store.getters.isLoggedIn) {
      this.loadData();
    } else {
      this.$store.commit("unsetCurrentUser");
    }
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    clearTimeout(this.edgeTimer);
    clearTimeout(this.longPressTimer);
    window.removeEventListener("mousemove", this.onPanMove);
    window.removeEventListener("mouseup", this.onPanEnd);
    window.removeEventListener("mousemove", this.onSelectMove);
    window.removeEventListener("mouseup", this.onSelectEnd);
  },
  computed: {
    // Мобильный адаптив (Capacitor/узкие экраны)
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    cellWidth() {
      return this.isMobile ? 64 : 90;
    },
    rangeLength() {
      if (!this.cellMenu.dateEnd || !this.cellMenu.date) return 1;
      return this.cellMenu.dateEnd.diff(this.cellMenu.date, "days") + 1;
    },
    ghostStyle() {
      const low = Math.min(this.selection.anchorIndex, this.selection.endIndex);
      const high = Math.max(this.selection.anchorIndex, this.selection.endIndex);
      return {
        left: `${low * this.cellWidth}px`,
        width: `${(high - low + 1) * this.cellWidth}px`,
      };
    },
    days() {
      return generateBottomLevelDates(this.viewStart, this.viewEnd, "daily").map((day) => ({
        ...day,
        isWeekend: day.date.isoWeekday() >= 6,
      }));
    },
    months() {
      return generateTopLevelDates(this.viewStart, this.viewEnd, "daily");
    },
    totalWidth() {
      return this.days.length * this.cellWidth;
    },
    todayOffsetPx() {
      return calendarDaysBetween(this.viewStart.format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")) * this.cellWidth;
    },
    todayVisible() {
      return moment().isBetween(this.viewStart, this.viewEnd, "day", "[]");
    },
    activeTemplates() {
      return this.templates.filter((t) => t.active);
    },
    // Режим «+ вид работы»: только целодневные рабочие шаблоны (v2: часы и
    // отсутствия конфликтуют с целодневной записью дня)
    menuTemplates() {
      if (!this.cellMenu.workingOnly) return this.activeTemplates;
      return this.activeTemplates.filter(
        (t) => WORKING_CATEGORIES.includes(t.category) && (t.start_minute === null || t.start_minute === undefined)
      );
    },
    templateColorPresets() {
      return ["#4caf50", "#e2850a", "#673ab7", "#1976d2", "#d2c619", "#b7b8b4", "#e91e63", "#00bcd4", "#795548", "#607d8b"];
    },
    workingCategoryItems() {
      return WORKING_CATEGORIES.map((category) => ({
        text: this.$t(categoryLocaleKey(category)),
        value: category,
      }));
    },
    allCategoryItems() {
      return WORKING_CATEGORIES.concat(ABSENCE_CATEGORIES).map((category) => ({
        text: this.$t(categoryLocaleKey(category)),
        value: category,
      }));
    },
    // REQ-07: адаптер + assignLanes (booking-shaped вход); пересечения смен одного
    // сотрудника запрещены сервером, поэтому lane фактически всегда 0 —
    // кроме валидной пары «ночной хвост + целодневная след. дня» (строка растёт)
    laneItemsByUser() {
      const grouped = {};
      this.shifts.forEach((shift) => {
        if (!grouped[shift.user_id]) grouped[shift.user_id] = [];
        grouped[shift.user_id].push(shift);
      });
      const result = {};
      const anchor = this.viewStart.format("YYYY-MM-DD");
      Object.entries(grouped).forEach(([userId, userShifts]) => {
        result[userId] = buildLaneItems(userShifts, assignLanes, anchor);
      });
      return result;
    },
    laneCountByUser() {
      const counts = {};
      Object.entries(this.laneItemsByUser).forEach(([userId, items]) => {
        counts[userId] = Math.max(1, ...items.map((item) => (item.lane || 0) + 1));
      });
      return counts;
    },
    canManage() {
      return (
        this.$store.getters.isSuperAdmin ||
        this.$store.getters.isAdmin ||
        this.$store.getters.isManager
      );
    },
    paywalled() {
      if (this.paywalledByApi) return true;
      const mainCompany = this.$store.getters.getMainCompany;
      if (!mainCompany || !mainCompany.plan || !mainCompany.plan.included_services) return false;
      if (mainCompany.plan.trial_end && moment(mainCompany.plan.trial_end).isAfter(moment())) return false;
      return !mainCompany.plan.included_services.includes("workers_schedule");
    },
    tooltipTemplateName() {
      const shift = this.tooltip.shift;
      if (!shift || !shift.shift_template_id) return null;
      const template = this.templates.find((t) => t.id === shift.shift_template_id);
      return template ? this.displayName(template) : null;
    },
  },
  watch: {
    "cellMenu.show"(open) {
      if (!open) {
        this.selection = { pending: false, active: false, user: null, anchorIndex: 0, endIndex: 0, startClientX: 0, rowRectLeft: 0 };
      }
    },
  },
  methods: {
    isAllDay,
    hoursLabel,
    categoryLocaleKey,
    displayName(template) {
      return templateDisplayName(template, (key) => this.$t(key));
    },
    authHeaders() {
      return { Authorization: this.$store.getters.getAuthToken };
    },
    fetchShifts(from, to) {
      return this.axios
        .get("/api/v1/shifts", {
          params: { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") },
          headers: this.authHeaders(),
        })
        .then((response) => response.data);
    },
    loadData() {
      this.isLoading = true;
      Promise.all([
        this.fetchShifts(this.viewStart, this.viewEnd),
        this.axios.get("/api/v1/shift_templates", { headers: this.authHeaders() }),
      ])
        .then(([batch, templatesResponse]) => {
          this.users = batch.users;
          this.shifts = batch.shifts;
          this.templates = templatesResponse.data;
          // Стартуем на «сегодня»: слева видна прошлая неделя, upor в край
          // срабатывает штатным расширением
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              const area = this.$refs.scrollArea;
              if (!area) return;
              const visible = area.clientWidth || 1200;
              area.scrollLeft = Math.max(0, this.todayOffsetPx - visible / 3);
            });
          });
        })
        .catch((error) => this.handleApiError(error))
        .finally(() => (this.isLoading = false));
    },
    // Ленивая догрузка (REQ-07): расширение окна у края, догружается только новый кусок
    onScroll() {
      clearTimeout(this.edgeTimer);
      this.edgeTimer = setTimeout(this.checkEdges, EDGE_DEBOUNCE_MS);
    },
    // Колесо/тачпад у упора: scroll-событие не стреляет — толкаем расширение сами
    onWheel(event) {
      const area = this.$refs.scrollArea;
      if (!area) return;
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : (event.shiftKey ? event.deltaY : 0);
      if (horizontal < 0 && area.scrollLeft <= 0) this.expandEdge("left");
      if (horizontal > 0 && area.scrollLeft + area.clientWidth >= area.scrollWidth - 1) this.expandEdge("right");
    },
    checkEdges() {
      const area = this.$refs.scrollArea;
      if (!area) return;
      // Занято загрузкой — перевзводим проверку (пользователь мог упереться в край
      // во время initial load; скролл-событий больше не будет)
      if (this.isLoadingEdge || this.isLoading || this.selection.active || this.selection.pending) {
        this.edgeTimer = setTimeout(this.checkEdges, 200);
        return;
      }
      const rightDistance = area.scrollWidth - area.scrollLeft - area.clientWidth;
      if (area.scrollLeft < EDGE_THRESHOLD_PX) {
        this.expandEdge("left");
      } else if (rightDistance < EDGE_THRESHOLD_PX) {
        this.expandEdge("right");
      }
    },
    expandEdge(edge) {
      if (this.isLoadingEdge) return;
      this.isLoadingEdge = true;
      const range = expandRange(edge, this.viewStart, this.viewEnd);
      this.fetchShifts(range.fetchFrom, range.fetchTo)
        .then((batch) => {
          this.viewStart = range.viewStart;
          this.viewEnd = range.viewEnd;
          this.shifts = mergeShifts(this.shifts, batch.shifts);
          if (edge === "left") {
            // Контент вырос влево — удерживаем экран на месте
            this.$nextTick(() => {
              const area = this.$refs.scrollArea;
              if (area) area.scrollLeft += EXPAND_STEP_DAYS * this.cellWidth;
            });
          }
        })
        .catch((error) => this.handleApiError(error))
        .finally(() => (this.isLoadingEdge = false));
    },
    scrollToToday() {
      if (!this.todayVisible) {
        this.viewStart = moment().subtract(37, "days").startOf("day");
        this.viewEnd = moment().add(30, "days").endOf("day");
        this.loadData();
        this.$nextTick(() => this.centerOnToday());
        return;
      }
      this.centerOnToday();
    },
    centerOnToday() {
      const area = this.$refs.scrollArea;
      if (!area) return;
      area.scrollTo({ left: Math.max(0, this.todayOffsetPx - area.clientWidth / 3), behavior: "smooth" });
    },
    // Grab-скролл: панорамирование мышью, клик подавляется после реального drag'а
    onPanStart(event) {
      if (event.button !== 0) return;
      const area = this.$refs.scrollArea;
      this.drag = { dragging: true, moved: false, startX: event.clientX, startScroll: area.scrollLeft };
      window.addEventListener("mousemove", this.onPanMove);
      window.addEventListener("mouseup", this.onPanEnd);
    },
    onPanMove(event) {
      if (!this.drag.dragging) return;
      const area = this.$refs.scrollArea;
      const dx = event.clientX - this.drag.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) this.drag.moved = true;
      if (this.drag.moved) {
        area.scrollLeft = this.drag.startScroll - dx;
        // Упор при панорамировании: тянут дальше края — расширяем
        if (area.scrollLeft <= 0 && dx > 40) this.expandEdge("left");
        if (area.scrollLeft + area.clientWidth >= area.scrollWidth - 1 && dx < -40) this.expandEdge("right");
      }
    },
    onPanEnd() {
      window.removeEventListener("mousemove", this.onPanMove);
      window.removeEventListener("mouseup", this.onPanEnd);
      // moved сбрасываем после click-фазы, чтобы подавить клик по окончании drag'а
      setTimeout(() => { this.drag = { dragging: false, moved: false, startX: 0, startScroll: 0 }; }, 0);
    },
    rowHeight(user) {
      return 6 + 28 * (this.laneCountByUser[user.id] || 1);
    },
    rowClasses(user, index) {
      return {
        "shift-timeline__row--even": index % 2 === 1,
        "shift-timeline__row--hover": this.hoverUserId === user.id,
      };
    },
    nameCellClasses(user, index) {
      return {
        "shift-timeline__name-cell--even": index % 2 === 1,
        "shift-timeline__name-cell--hover": this.hoverUserId === user.id,
      };
    },
    dayClasses(day) {
      return {
        "shift-timeline__day--today": day.date.isSame(moment(), "day"),
        "shift-timeline__day--weekend": day.isWeekend,
      };
    },
    initials(user) {
      const source = user.name || user.email || "";
      return source
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
    },
    isAbsence(shift) {
      return ABSENCE_CATEGORIES.includes(shift.category);
    },
    isAbsenceCategory(category) {
      return ABSENCE_CATEGORIES.includes(category);
    },
    barStyle(item) {
      const { left: baseLeft, width: baseWidth } = shiftBarMetrics(
        item.shift, this.viewStart.format("YYYY-MM-DD"), this.cellWidth
      );
      const count = item.sliceCount || 1;
      const index = item.sliceIndex || 0;
      // Split-день: целодневные виды дня делят ячейку вертикальными долями
      const sliceWidth = baseWidth / count;
      return {
        left: `${baseLeft + index * sliceWidth}px`,
        width: `${Math.max(2, sliceWidth - (count > 1 ? 2 : 0))}px`,
        top: `${4 + (item.lane || 0) * 28}px`,
        backgroundColor: this.shiftColor(item.shift),
      };
    },
    shiftColor(shift) {
      const template = this.templates.find((t) => t.id === shift.shift_template_id);
      return (template && template.color) || CATEGORY_COLORS[shift.category] || "#9e9e9e";
    },
    barLabel(shift) {
      return hoursLabel(shift) || this.$t(categoryLocaleKey(shift.category));
    },
    barTitle(shift) {
      if (!shift) return "";
      const category = this.$t(categoryLocaleKey(shift.category));
      const hours = hoursLabel(shift);
      return hours ? `${category} ${hours}` : category;
    },
    durationLabel(shift) {
      const hours = Math.floor(shift.duration_minutes / 60);
      const minutes = shift.duration_minutes % 60;
      return minutes
        ? `${hours} ${this.$t("shift_timeline.hours_short")} ${minutes} ${this.$t("shift_timeline.minutes_short")}`
        : `${hours} ${this.$t("shift_timeline.hours_short")}`;
    },
    onBarEnter(event, shift) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltip = { show: true, x: event.clientX + 8, y: rect.bottom + 6, shift };
    },
    onBarLeave() {
      this.tooltip = { show: false, x: 0, y: 0, shift: null };
    },
    templateHours(template) {
      if (template.start_minute === null || template.start_minute === undefined) return "";
      return `${minutesLabel(template.start_minute)}–${minutesLabel(template.start_minute + template.duration_minutes)}`;
    },
    dayIndexAt(clientX) {
      const offsetX = clientX - this.selection.rowRectLeft;
      const max = this.days.length - 1;
      return Math.min(max, Math.max(0, Math.floor(offsetX / this.cellWidth)));
    },
    // Mouse-протяжка: drag >5px по строке = выделение периода
    onSelectStart(event, user) {
      if (!this.canManage || event.button !== 0) return;
      const rectLeft = event.currentTarget.getBoundingClientRect().left;
      this.selection = {
        pending: true, active: false, user,
        anchorIndex: 0, endIndex: 0,
        startClientX: event.clientX, rowRectLeft: rectLeft,
      };
      this.selection.anchorIndex = this.dayIndexAt(event.clientX);
      this.selection.endIndex = this.selection.anchorIndex;
      window.addEventListener("mousemove", this.onSelectMove);
      window.addEventListener("mouseup", this.onSelectEnd);
    },
    onSelectMove(event) {
      if (!this.selection.pending && !this.selection.active) return;
      if (!this.selection.active && Math.abs(event.clientX - this.selection.startClientX) > 5) {
        this.selection.active = true;
        this.selection.pending = false;
      }
      if (this.selection.active) {
        this.selection.endIndex = this.dayIndexAt(event.clientX);
      }
    },
    onSelectEnd(event) {
      window.removeEventListener("mousemove", this.onSelectMove);
      window.removeEventListener("mouseup", this.onSelectEnd);
      if (this.selection.active) {
        this.suppressRowClick = true;
        // Синтезированный click после mouseup — «клик снаружи» для v-menu:
        // открываем меню ПОСЛЕ click-фазы, иначе Vuetify закрывает его мгновенно
        const { clientX, clientY } = event;
        setTimeout(() => this.openRangeMenu(clientX, clientY), 0);
      } else {
        this.selection.pending = false;
      }
    },
    // Touch: long-press 400мс → протяжка; короткий тап = клик; свайп = нативный скролл
    onTouchStart(event, user) {
      if (!this.canManage || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const rectLeft = event.currentTarget.getBoundingClientRect().left;
      this.touchOrigin = { x: touch.clientX, y: touch.clientY };
      clearTimeout(this.longPressTimer);
      this.longPressTimer = setTimeout(() => {
        this.selection = {
          pending: false, active: true, user,
          anchorIndex: 0, endIndex: 0,
          startClientX: touch.clientX, rowRectLeft: rectLeft,
        };
        this.selection.anchorIndex = this.dayIndexAt(touch.clientX);
        this.selection.endIndex = this.selection.anchorIndex;
      }, 400);
    },
    onTouchMove(event) {
      const touch = event.touches[0];
      if (this.selection.active) {
        event.preventDefault(); // протяжка вместо скролла
        this.selection.endIndex = this.dayIndexAt(touch.clientX);
        return;
      }
      // Палец поехал до long-press — это скролл, отменяем таймер
      if (Math.abs(touch.clientX - this.touchOrigin.x) > 10 || Math.abs(touch.clientY - this.touchOrigin.y) > 10) {
        clearTimeout(this.longPressTimer);
      }
    },
    onTouchEnd(event) {
      clearTimeout(this.longPressTimer);
      if (this.selection.active) {
        event.preventDefault(); // подавить синтезированный click
        this.suppressRowClick = true;
        const touch = event.changedTouches[0];
        setTimeout(() => this.openRangeMenu(touch.clientX, touch.clientY), 0);
      }
    },
    openAddView() {
      const shift = this.barMenu.shift;
      this.barMenu = { show: false, x: this.barMenu.x, y: this.barMenu.y, shift: null };
      setTimeout(() => {
        this.cellMenu = {
          show: true, x: this.barMenu.x, y: this.barMenu.y,
          user: this.users.find((u) => u.id === shift.user_id) || { id: shift.user_id },
          date: moment(shift.date, "YYYY-MM-DD"),
          dateEnd: null,
          workingOnly: true,
        };
      }, 0);
    },
    onKeydown(event) {
      const tag = event.target && event.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || this.customDialog.show || this.manageDialog.show || this.templateForm.show) return;
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const area = this.$refs.scrollArea;
        if (!area) return;
        event.preventDefault();
        if (event.key === "ArrowLeft" && area.scrollLeft <= 0) {
          this.expandEdge("left"); // упор: событие scroll не стрельнёт
          return;
        }
        if (event.key === "ArrowRight" && area.scrollLeft + area.clientWidth >= area.scrollWidth - 1) {
          this.expandEdge("right");
          return;
        }
        const delta = 7 * this.cellWidth * (event.key === "ArrowLeft" ? -1 : 1);
        area.scrollBy({ left: delta, behavior: "smooth" });
      }
    },
    // ── Управление каталогом видов работ (CRUD шаблонов) ──
    openTemplateForm(template) {
      if (template) {
        // Сид-шаблон префиллим локализованным именем; нетронутое имя при
        // сохранении не отправляем, чтобы не демоутить system на бэке
        const shownName = this.displayName(template);
        this.templateForm = {
          show: true, id: template.id, name: shownName, initialName: shownName, category: template.category,
          allDay: template.start_minute === null || template.start_minute === undefined,
          start: template.start_minute != null ? minutesLabel(template.start_minute) : "09:00",
          end: template.start_minute != null ? minutesLabel(template.start_minute + template.duration_minutes) : "18:00",
          color: template.color || "#4caf50",
        };
      } else {
        this.templateForm = { show: true, id: null, name: "", initialName: null, category: "work", allDay: true, start: "09:00", end: "18:00", color: "#4caf50" };
      }
    },
    saveTemplateForm() {
      const form = this.templateForm;
      const payload = { name: form.name, category: form.category, color: form.color };
      if (form.id && form.name === form.initialName) delete payload.name;
      if (form.allDay || ABSENCE_CATEGORIES.includes(form.category)) {
        payload.start_minute = null;
        payload.duration_minutes = null;
      } else {
        const start = parseTimeToMinutes(form.start);
        const end = parseTimeToMinutes(form.end);
        if (start === null || end === null) {
          this.$notify(this.$t("shift_timeline.bad_time_format"), "", "warning");
          return;
        }
        payload.start_minute = start;
        payload.duration_minutes = end > start ? end - start : end + MINUTES_PER_DAY - start;
      }
      const request = form.id
        ? this.axios.patch(`/api/v1/shift_templates/${form.id}`, { shift_template: payload }, { headers: this.authHeaders() })
        : this.axios.post("/api/v1/shift_templates", { shift_template: { ...payload, position: this.templates.length } }, { headers: this.authHeaders() });
      request
        .then((response) => {
          if (form.id) {
            this.templates = this.templates.map((t) => (t.id === form.id ? response.data : t));
          } else {
            this.templates.push(response.data);
          }
          this.templateForm = { ...this.templateForm, show: false };
        })
        .catch((error) => this.handleApiError(error));
    },
    toggleTemplateActive(template) {
      this.axios
        .patch(`/api/v1/shift_templates/${template.id}`, { shift_template: { active: !template.active } }, { headers: this.authHeaders() })
        .then((response) => {
          this.templates = this.templates.map((t) => (t.id === template.id ? response.data : t));
        })
        .catch((error) => this.handleApiError(error));
    },
    deleteTemplate(template) {
      this.axios
        .delete(`/api/v1/shift_templates/${template.id}`, { headers: this.authHeaders() })
        .then(() => {
          this.templates = this.templates.filter((t) => t.id !== template.id);
        })
        .catch((error) => this.handleApiError(error));
    },
    openRangeMenu(x, y) {
      this.skipWeekends = false;
      const low = Math.min(this.selection.anchorIndex, this.selection.endIndex);
      const high = Math.max(this.selection.anchorIndex, this.selection.endIndex);
      this.cellMenu = {
        show: true, x, y,
        user: this.selection.user,
        date: this.viewStart.clone().add(low, "days"),
        dateEnd: this.viewStart.clone().add(high, "days"),
      };
    },
    createBulk(attrs) {
      const { user, date, dateEnd } = this.cellMenu;
      const skipWeekends = this.skipWeekends;
      this.cellMenu = { ...this.cellMenu, show: false };
      this.isLoading = true;
      this.axios
        .post(
          "/api/v1/shifts/bulk",
          { shift: { user_id: user.id, from: date.format("YYYY-MM-DD"), to: dateEnd.format("YYYY-MM-DD"),
                     skip_weekends: skipWeekends, ...attrs } },
          { headers: this.authHeaders() }
        )
        .then((response) => {
          this.shifts = mergeShifts(this.shifts, response.data.created);
          const skipped = response.data.skipped.length;
          if (skipped) {
            this.$notify(
              this.$t("shift_timeline.bulk_result", { created: response.data.created.length, skipped }),
              "", "info", 4000
            );
          }
        })
        .catch((error) => this.handleApiError(error))
        .finally(() => (this.isLoading = false));
    },
    // Роли в UI: без прав мутаций ячейки read-only (REQ-07, как checkAdminManager)
    onRowClick(event, user) {
      if (this.suppressRowClick) {
        this.suppressRowClick = false;
        return;
      }
      if (!this.canManage || this.drag.moved) return;
      // rect ряда лежит ВНУТРИ скролл-контейнера: его left уже смещён на -scrollLeft,
      // поэтому clientX - rect.left даёт offset в координатах контента как есть
      const rect = event.currentTarget.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const dayIndex = Math.floor(offsetX / this.cellWidth);
      const date = this.viewStart.clone().add(dayIndex, "days");
      // Открытие после click-фазы: bubbling текущего клика не должен стать
      // «кликом снаружи» для v-menu (см. openRangeMenu)
      const { clientX, clientY } = event;
      setTimeout(() => {
        this.cellMenu = { show: true, x: clientX, y: clientY, user, date, dateEnd: null };
      }, 0);
    },
    onBarClick(event, shift) {
      if (!this.canManage || this.drag.moved) return;
      this.onBarLeave();
      this.barMenu = { show: true, x: event.clientX, y: event.clientY, shift };
    },
    createFromTemplate(template) {
      if (this.rangeLength > 1) {
        this.createBulk({ shift_template_id: template.id });
      } else {
        this.cellMenu = { ...this.cellMenu, show: false };
        this.createShift({ shift_template_id: template.id });
      }
    },
    openCustomDialog() {
      // dateEnd/чекбокс переживают диалог (вотчер чистит только ghost)
      this.cellMenu = { ...this.cellMenu, show: false };
      this.customDialog = { show: true, category: "work", start: "09:00", end: "15:00" };
    },
    createCustom() {
      const start = parseTimeToMinutes(this.customDialog.start);
      const end = parseTimeToMinutes(this.customDialog.end);
      if (start === null || end === null) {
        this.$notify(this.$t("shift_timeline.bad_time_format"), "", "warning");
        return;
      }
      // конец раньше/равен началу = смена через полночь (ночная)
      const duration = end > start ? end - start : end + MINUTES_PER_DAY - start;
      this.customDialog.show = false;
      const attrs = {
        category: this.customDialog.category,
        start_minute: start,
        duration_minutes: duration,
      };
      if (this.rangeLength > 1) {
        this.createBulk(attrs);
      } else {
        this.createShift(attrs);
      }
    },
    createShift(attrs) {
      const { user, date } = this.cellMenu;
      if (!user || !date) return;
      this.isLoading = true;
      this.axios
        .post(
          "/api/v1/shifts",
          { shift: { user_id: user.id, date: date.format("YYYY-MM-DD"), ...attrs } },
          { headers: this.authHeaders() }
        )
        .then((response) => {
          this.shifts.push(response.data);
        })
        .catch((error) => this.handleApiError(error))
        .finally(() => (this.isLoading = false));
    },
    deleteShift(shift) {
      if (!shift) return;
      this.isLoading = true;
      this.axios
        .delete(`/api/v1/shifts/${shift.id}`, { headers: this.authHeaders() })
        .then(() => {
          this.shifts = this.shifts.filter((s) => s.id !== shift.id);
        })
        .catch((error) => this.handleApiError(error))
        .finally(() => (this.isLoading = false));
    },
    handleApiError(error) {
      const code = error && error.response && error.response.data && error.response.data.error;
      if (code === "paywall") {
        this.paywalledByApi = true;
        return;
      }
      const known = ["overlap", "all_day_conflict", "absence_not_all_day", "invalid_interval", "invalid_range", "validation_failed"];
      const text = known.includes(code)
        ? this.$t(`shift_timeline.errors.${code}`)
        : this.$t("errors.edit_schedule");
      // Общие Vuetify-уведомления вместо SweetAlert (курс репо, решение USER)
      this.$notify(text, "", "error");
    },
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.shift-timeline-viewport {
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
}
.shift-timeline {
  display: flex;
  min-height: 120px;
}
.shift-timeline__names {
  flex: 0 0 200px;
  border-right: 1px solid #d0d0d0;
  background: #fafafa;
}
@media (max-width: 960px) {
  .shift-timeline__names {
    flex: 0 0 120px;
  }
  .shift-timeline__name-text {
    font-size: 12px;
  }
}
.shift-timeline__names-header {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 52px;
  background: #fafafa;
  border-bottom: 1px solid #d0d0d0;
}
.shift-timeline__name-cell {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-bottom: 1px solid #ececec;
  font-size: 13px;
  transition: background-color 0.15s;
}
.shift-timeline__name-cell--even {
  background: #f4f5f7;
}
.shift-timeline__name-cell--hover {
  background: rgba(25, 118, 210, 0.08);
}
.shift-timeline__avatar {
  flex: 0 0 26px;
  height: 26px;
  margin-right: 8px;
  border-radius: 50%;
  background: #90a4ae;
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.shift-timeline__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.shift-timeline__name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.shift-timeline__scroll {
  overflow-x: auto;
  flex: 1 1 auto;
  position: relative;
  cursor: grab;
}
.shift-timeline__scroll--grabbing {
  cursor: grabbing;
  user-select: none;
}
.shift-timeline__months {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  height: 24px;
  background: #fafafa;
}
.shift-timeline__month {
  border-right: 1px solid #d0d0d0;
  border-bottom: 1px solid #ececec;
  font-size: 12px;
  font-weight: 600;
  padding-left: 6px;
  white-space: nowrap;
  overflow: hidden;
}
.shift-timeline__days {
  position: sticky;
  top: 24px;
  z-index: 3;
  display: flex;
  height: 28px;
  background: #fafafa;
  border-bottom: 1px solid #d0d0d0;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.06);
}
.shift-timeline__day {
  flex: 0 0 auto;
  border-right: 1px solid #ececec;
  font-size: 12px;
  text-align: center;
  line-height: 28px;
}
.shift-timeline__day--weekend {
  background: #f1f3f5;
  color: #78909c;
}
.shift-timeline__day--today {
  background: rgba(25, 118, 210, 0.14);
  font-weight: bold;
  color: #1976d2;
}
.shift-timeline__body {
  position: relative;
}
.shift-timeline__today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: #1976d2;
  opacity: 0.55;
  z-index: 1;
  pointer-events: none;
}
.shift-timeline__row {
  position: relative;
  display: flex;
  border-bottom: 1px solid #ececec;
  cursor: pointer;
  transition: background-color 0.15s;
}
.shift-timeline__row--even {
  background: #fafbfc;
}
.shift-timeline__row--hover {
  background: rgba(25, 118, 210, 0.06);
}
.shift-timeline__grid-cell {
  flex: 0 0 auto;
  border-right: 1px solid #f0f0f0;
}
.shift-timeline__grid-cell--weekend {
  background: rgba(120, 144, 156, 0.08);
}
.shift-timeline__bar {
  position: absolute;
  height: 26px;
  border-radius: 5px;
  color: white;
  font-size: 11px;
  line-height: 26px;
  padding: 0 6px;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: filter 0.15s, box-shadow 0.15s, transform 0.15s;
}
.shift-timeline__bar:hover {
  filter: brightness(1.08);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}
.shift-timeline__bar--absence {
  opacity: 0.85;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.18) 0,
    rgba(255, 255, 255, 0.18) 6px,
    transparent 6px,
    transparent 12px
  );
}
.shift-timeline__bar-label {
  pointer-events: none;
}
.shift-timeline__ghost {
  position: absolute;
  top: 2px;
  bottom: 2px;
  background: rgba(25, 118, 210, 0.18);
  border: 1px dashed #1976d2;
  border-radius: 5px;
  z-index: 1;
  pointer-events: none;
}
.shift-timeline__pan-zone {
  cursor: grab;
}
.shift-timeline__empty {
  position: absolute;
  top: 24px;
  left: 0;
  right: 0;
  text-align: center;
  color: #90a4ae;
  font-size: 13px;
  pointer-events: none;
}
.shift-timeline__edge-loader {
  position: sticky;
  left: 8px;
  bottom: 8px;
  display: inline-block;
  padding: 2px;
}
.shift-timeline__tooltip {
  position: fixed;
  z-index: 20;
  background: rgba(33, 33, 33, 0.95);
  color: #fff;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.5;
  pointer-events: none;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
  max-width: 240px;
}
.shift-timeline__tooltip-title {
  font-weight: 600;
}
.shift-timeline__tooltip-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 5px;
}
.shift-timeline__tooltip-muted {
  color: #b0bec5;
}

/* Тёмная тема */
.shift-timeline--dark {
  border-color: #555;
}
.shift-timeline--dark .shift-timeline__names,
.shift-timeline--dark .shift-timeline__names-header,
.shift-timeline--dark .shift-timeline__months,
.shift-timeline--dark .shift-timeline__days {
  background: #1e1e1e;
}
.shift-timeline--dark .shift-timeline__names,
.shift-timeline--dark .shift-timeline__names-header,
.shift-timeline--dark .shift-timeline__month,
.shift-timeline--dark .shift-timeline__days {
  border-color: #555;
}
.shift-timeline--dark .shift-timeline__name-cell {
  border-color: #383838;
}
.shift-timeline--dark .shift-timeline__name-cell--even,
.shift-timeline--dark .shift-timeline__row--even {
  background: #252525;
}
.shift-timeline--dark .shift-timeline__name-cell--hover,
.shift-timeline--dark .shift-timeline__row--hover {
  background: rgba(144, 202, 249, 0.12);
}
.shift-timeline--dark .shift-timeline__row,
.shift-timeline--dark .shift-timeline__grid-cell,
.shift-timeline--dark .shift-timeline__day {
  border-color: #383838;
}
.shift-timeline--dark .shift-timeline__day--weekend {
  background: rgba(255, 255, 255, 0.05);
}
.shift-timeline--dark .shift-timeline__day--today {
  background: rgba(144, 202, 249, 0.2);
  color: #90caf9;
}
.shift-timeline--dark .shift-timeline__grid-cell--weekend {
  background: rgba(255, 255, 255, 0.04);
}
.shift-timeline--dark .shift-timeline__today-line {
  background: #90caf9;
}
.shift-timeline--dark .shift-timeline__ghost {
  background: rgba(144, 202, 249, 0.2);
  border-color: #90caf9;
}
</style>
